import React from 'react';

import { Card } from '@/components/ui/cards';
import { Text } from '@/components/ui/text/Text';
import { HourCounterBadge } from '@/components/ui/badges';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { PermissionGuard } from '@/components/layout';
import { colors } from '@/assets/styles/colors';
import { useTranslation } from '@/i18n';
import { ShiftDayCard } from './ShiftDayCard';
import type { UseShiftWeeklyDistributionResult } from '@/hooks/shift/useShiftWeeklyDistribution';


interface ShiftWeeklyDistributionGridProps extends UseShiftWeeklyDistributionResult {
  onCancel: () => void;
}

export const ShiftWeeklyDistributionGrid: React.FC<ShiftWeeklyDistributionGridProps> = ({
  weeks,
  showWeekHeaders,
  cycleCounter,
  isSaveDisabled,
  isSaving,
  openAssignPanel,
  onSave,
  onCancel,
}) => {
  const { t: translate } = useTranslation('schedule');

  return (
    <div className="mt-4" data-testid="shift-weekly-distribution-grid">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <Text variant="body-title-medium" as="h2">
            {translate('shift.create.sections.weeklyDistribution')}
          </Text>
          {cycleCounter && (
            <HourCounterBadge value={cycleCounter.value} variant={cycleCounter.variant} />
          )}
        </div>

        {weeks.map((week) => (
          <div key={week.weekNumber} className="mb-8">
            {showWeekHeaders && (
              <div className="flex items-center justify-between mb-2">
                <Text variant="subheader-medium" color={colors.primary.dash}>
                  {translate('shift.create.assignSchedule.weekHeader', { week: week.weekNumber })}
                </Text>
                {week.counter && (
                  <HourCounterBadge value={week.counter.value} variant={week.counter.variant} />
                )}
              </div>
            )}

            <div className="grid grid-cols-7 gap-4">
              {week.days.map((day) => (
                <ShiftDayCard key={day.daySequence} day={day} onClick={openAssignPanel} />
              ))}
            </div>
          </div>
        ))}

        <div className="flex justify-center gap-4 mt-4">
          <PrimaryButton variant="outline" onClick={onCancel} data-testid="shift-grid-cancel-button">
            {translate('common:actions.cancel')}
          </PrimaryButton>
          <PermissionGuard action="create">
            <PrimaryButton
              onClick={onSave}
              disabled={isSaveDisabled}
              isLoading={isSaving}
              data-testid="shift-grid-save-button"
            >
              {translate('common:actions.save')}
            </PrimaryButton>
          </PermissionGuard>
        </div>
      </Card>
    </div>
  );
};

export default ShiftWeeklyDistributionGrid;
